// Force /main/ prefix for all main app URLs
if (!window.location.pathname.startsWith('/main') && !window.location.pathname.startsWith('/form')) {
  let newPath = '/main' + window.location.pathname;
  if (newPath === '/main') {
    newPath = '/main/';
  }
  window.location.replace(newPath + window.location.search + window.location.hash);
}

import { createApp } from "vue";
import App from "./App.vue";
import VueMatomo from 'vue-matomo';
import store from "./store";
import router from "./router";
import "./assets/css/nucleo-icons.css";
import "./assets/css/nucleo-svg.css";
import "./assets/css/custom-filters.css";
import 'select2/dist/css/select2.min.css';
import ArgonDashboard from "./argon-dashboard";
import Toast from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-bootstrap.css';
import { imgFallbackDirective } from "./utils/image-handler"
import './registerServiceWorker'
import { purgeObsoleteKeys, clearAllCache } from './utils/cacheService.js'
import * as errorLogger from './utils/errorLogger.js'

const initApp = async () => {
  // Check if page was hard-refreshed / reloaded, and if so, clear IndexedDB
  try {
    const navEntries = performance.getEntriesByType("navigation");
    if (navEntries.length > 0 && navEntries[0].type === "reload") {
      console.log('[IDB] Page reload detected, clearing IndexedDB cache...');
      await clearAllCache();
    }
  } catch (e) {
    console.warn("Performance API not supported, skipping reload check", e);
  }

  // Silently clean up any IDB keys from old naming conventions (e.g. dropdown_index_*)
  await purgeObsoleteKeys();

  // Initialize error logger with store + API base URL
  const apiBase = process.env.VUE_APP_API_URL || '';
  errorLogger.init(store, apiBase);

  // Sync any errors queued while offline on startup
  errorLogger.syncQueue();

  const appInstance = createApp(App);
  appInstance.directive('img-fallback', imgFallbackDirective);
  appInstance.use(store);
  appInstance.use(router);
  appInstance.use(ArgonDashboard);
  appInstance.use(Toast, {
    position: 'top-right'
  });
  appInstance.use(VueMatomo, {
    host: 'https://matomo.vmilesup.in',
    siteId: process.env.VUE_APP_MATOMO_SITE_ID || 3,
    enableLinkTracking: true,
    trackInitialView: true,
  });

  // Vue component error handler
  appInstance.config.errorHandler = (err, instance, info) => {
    errorLogger.logError(err?.message || String(err), err?.stack || null, {
      action: info,
      context: { component: instance?.$options?.name || 'unknown' },
    });
    console.error('[Vue Error]', err, info);
  };

  appInstance.mount("#app");

  document.title = store.state.appTitle;

  // Global JS error handler (catches errors outside Vue)
  window.onerror = (message, source, lineno, colno, error) => {
    errorLogger.logError(String(message), error?.stack || null, {
      action: 'window.onerror',
      context: { source, lineno, colno },
    });
    return false; // don't suppress the error
  };

  // Unhandled Promise rejection handler
  window.onunhandledrejection = (event) => {
    const err = event.reason;
    errorLogger.logError(
      err?.message || String(err) || 'Unhandled Promise Rejection',
      err?.stack || null,
      { action: 'unhandledrejection' }
    );
  };

  // Sync queued errors when coming back online
  window.addEventListener('online', () => errorLogger.syncQueue());
};

initApp();

